import { AxiosInstance } from 'axios'
import { ChatFileResponse } from '@api/generated/internal/internalApi'
import { MessageType } from '@constants/agentTypes'

type ApiMessageRole = MessageType | 'toolCall' | 'toolMessage' | 'toolError'

// Typing this in the frontend for now, looking to OpenAPI spec in the future
export type ToolCallApproval = {
  status: 'pending' | 'approved' | 'rejected' | 'completed'
  reason?: string
  resolvedAt?: string
  resolvedBy?: string
}

export type ToolCallContent = {
  id?: string
  type: 'toolCall'
  value: string
  data: {
    runId: string
    parentRunId?: string
    tool?: { name?: string; label?: string; id?: string }
  }
  approval?: ToolCallApproval
}

export type MessageFeedbackValue = 'approved' | 'disapproved'

export type MessageFeedback = {
  id: string
  messageId: string
  text: string | null
  value: MessageFeedbackValue | null
  timestamp: string
}

export type CreateMessageFeedbackRequest = {
  text?: string | null
  value?: MessageFeedbackValue | null
  timestamp: string
}

export type ApiMessage = {
  id: string
  role: ApiMessageRole
  chatflowId: string
  sessionId: string
  content: string | ToolCallContent
  createdDate: string
  // Machine-readable classification for llmError messages (e.g. RATE_LIMITED),
  // attached by the backend while the provider error was still structured (WF-2547).
  errorCode?: string
  pendingApproval?: boolean
  pendingBackground?: boolean
  childSessionId?: string
  childChatflowId?: string
  childChatflowName?: string
  approvalContinuationStarted?: boolean
  chatFiles: ChatFileResponse[]
  feedback?: MessageFeedback | null
}

export type CreateMessageRequest = Pick<ApiMessage, 'role' | 'content'> & {
  files?: string[]
}

export default class ChatmessageApi {
  constructor(private client: AxiosInstance) {}

  getChatmessageFromChatflow = (id: string, sessionId?: string) =>
    this.client.get<ApiMessage[]>(sessionId ? `/chatflows/${id}/sessions/${sessionId}/messages` : `/chatflows/${id}/messages`)

  createNewChatmessage = (id, body: CreateMessageRequest) => this.client.post<ApiMessage>(`/chatflows/${id}/messages`, body)

  deleteChatmessage = (id: string, sessionId?: string) =>
    this.client.delete(sessionId ? `/chatflows/${id}/sessions/${sessionId}/messages` : `/chatflows/${id}/messages`)

  approveToolCall = (chatflowId: string, sessionId: string, messageId: string, socketIOClientId?: string) =>
    this.client.post<ApiMessage>(`/chatflows/${chatflowId}/sessions/${sessionId}/messages/${messageId}/approve`, { socketIOClientId })

  rejectToolCall = (chatflowId: string, sessionId: string, messageId: string, reason?: string, socketIOClientId?: string) =>
    this.client.post<ApiMessage>(`/chatflows/${chatflowId}/sessions/${sessionId}/messages/${messageId}/reject`, {
      reason,
      socketIOClientId,
    })

  cancelSubTeammateExecution = (chatflowId: string, sessionId: string, messageId: string, reason?: string) =>
    this.client.post<{ canceled: boolean }>(
      `/chatflows/${chatflowId}/sessions/${sessionId}/messages/${messageId}/cancel-sub-teammate`,
      reason !== undefined ? { reason } : {}
    )

  acceptSubTeammateResult = (chatflowId: string, sessionId: string, messageId: string) =>
    this.client.post<{ accepted: boolean }>(`/chatflows/${chatflowId}/sessions/${sessionId}/messages/${messageId}/accept-sub-teammate`)

  submitSubTeammateFollowUp = (chatflowId: string, sessionId: string, messageId: string, message: string) =>
    this.client.post<{ dispatched: boolean }>(
      `/chatflows/${chatflowId}/sessions/${sessionId}/messages/${messageId}/sub-teammate-followup`,
      { message }
    )

  createMessageFeedback = (messageId: string, body: CreateMessageFeedbackRequest) =>
    this.client.post<MessageFeedback>(`/messages/${messageId}/feedback`, body)

  getMessageFeedback = (messageId: string) => this.client.get<MessageFeedback>(`/messages/${messageId}/feedback`)
}
